/**
 * Reader Credit & Billing Policy Page
 * 
 * Legal document for NewAudia Systems, LLC
 * Version 1.1 | Effective Date: March 1, 2026
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CreditCard, Mail } from 'lucide-react';
import UniversalFooter from '../components/UniversalFooter';
import { useAuth } from '../contexts/AuthContext';
import { Seo, PAGE_SEO } from '../components/Seo';

const ReaderCreditBillingPolicyPage = () => {
  const { user } = useAuth();
  
  const getHomeRoute = () => {
    if (!user) return '/';
    const roles = user.roles || [];
    if (roles.includes('admin')) return '/admin';
    if (roles.includes('publisher')) return '/publisher';
    if (roles.includes('author') || roles.includes('creator')) return '/creator';
    if (roles.includes('reader')) return '/home';
    return '/';
  };
  
  const homeRoute = getHomeRoute();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200">
      <Seo {...PAGE_SEO.readerCredits} />
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to={homeRoute} className="flex items-center gap-3">
            <img src="/newaudia-logo.jpg" alt="NewAudia" className="h-10 w-auto rounded-lg" />
          </Link>
          <Link 
            to={homeRoute}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Document Header */}
        <div className="mb-12 pb-8 border-b border-slate-700">
          <div className="flex items-center gap-4 mb-6">
            <img src="/newaudia-logo.jpg" alt="NewAudia" className="h-16 w-auto rounded-lg" />
            <div>
              <h1 data-testid="billing-company-name" className="text-3xl font-bold text-white">NewAudia Systems, LLC</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-3 mb-4">
            <CreditCard className="w-6 h-6 text-indigo-400" />
            <h2 data-testid="billing-title" className="text-2xl font-semibold text-white">Reader Credit & Billing Policy</h2>
          </div>
          
          <div className="flex flex-wrap gap-6 text-sm text-slate-400">
            <p><span className="text-slate-500">Effective Date:</span> March 1, 2026</p>
            <p><span className="text-slate-500">Version:</span> 1.1</p>
            <p><span className="text-slate-500">Last Updated:</span> March 2026</p>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-invert prose-slate max-w-none">
          {/* Important Notice */}
          <section className="mb-10">
            <p className="text-amber-400 font-semibold text-sm uppercase tracking-wide mb-4">Important — Please Read Carefully</p>
            <p className="text-slate-300 leading-relaxed">
              This Reader Credit & Billing Policy ("Policy") governs the acquisition, consumption, billing, and expiration of 
              Reader Credits within the NewAudia platform, website, applications, and services (collectively, the "Services"). 
              This Policy applies to all Readers and registered users who access content using the Reader Credit system. By 
              purchasing or using Reader Credits, you agree to this Policy in full.
            </p>
          </section>

          {/* Company Identification */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">Company Identification</h3>
            <p className="text-slate-300 leading-relaxed">
              The Services are operated by <strong className="text-white">NewAudia Systems, LLC</strong>, a limited liability 
              company organized under the laws of the State of Colorado, with its principal place of business in Colorado Springs, 
              Colorado (hereinafter "NewAudia," "Company," "we," "us," or "our"). "NewAudia" is a registered trade name of 
              NewAudia Systems, LLC in the State of Colorado.
            </p>
            <p className="text-slate-300 leading-relaxed mt-4">
              This Policy is incorporated by reference into the NewAudia Terms of Service. It operates alongside the NewAudia 
              Privacy Policy (governing data collected during billing), the Acceptable Use Policy (governing enforcement and 
              account actions), and the NewAudia Author Publishing Agreement (which governs how Reader Credit consumption 
              translates into Commission earnings for Authors and Publishers).
            </p>
          </section>

          {/* Section 1 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">1. Nature of Reader Credits</h3>
            <p className="text-slate-300 leading-relaxed mb-3">
              Reader Credits ("Credits") are the unit of access within NewAudia's subscription and purchasing system. Credits 
              are consumed when Readers access Audiobooks and interact with platform features. The following terms govern the 
              nature of Credits:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Credits represent access entitlements to platform content and features — they are not currency, stored value, or a financial instrument of any kind</li>
              <li>Credits have no cash value and cannot be redeemed for cash, transferred to another user, or exchanged for goods or services outside the NewAudia platform</li>
              <li>Credits are non-transferable between accounts unless expressly permitted by NewAudia in writing</li>
              <li>Credits are non-refundable except as required by applicable law or as expressly stated in Section 5 of this Policy</li>
            </ul>

            <div className="bg-emerald-950/30 border border-emerald-900/50 rounded-lg p-5 mt-6">
              <h4 className="text-lg font-semibold text-emerald-400 mb-2">Reader Credits and Author Earnings</h4>
              <p className="text-slate-300 leading-relaxed text-sm">
                When a Reader consumes Credits to access an Audiobook, fifty percent (50%) of the monetary value of those 
                Credits is allocated as a Commission to the Author or Publisher of that title, consistent with the NewAudia 
                Author Publishing Agreement. Reader credit activity directly supports the authors whose work you enjoy on the platform.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">2. Credit Acquisition</h3>
            <p className="text-slate-300 leading-relaxed mb-6">
              Credits may be obtained through the following channels:
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">2.1 Individual Purchase</h4>
            <p className="text-slate-300 leading-relaxed">
              Readers may purchase Credits directly through the NewAudia platform. All purchases are processed by Stripe, our 
              third-party payment processor. Payment card information is handled directly by Stripe and is not stored by NewAudia. 
              By completing a purchase, you authorize NewAudia and Stripe to charge your selected payment method for the amount 
              displayed at the time of transaction.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">2.2 Subscription Plans</h4>
            <p className="text-slate-300 leading-relaxed">
              NewAudia offers subscription plans that include a Credit allocation per billing period. Subscription pricing, Credit 
              allocations, and available tiers are published on the NewAudia Pricing page at{' '}
              <a href="https://www.newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">www.newaudia.com</a>. 
              By subscribing, you authorize NewAudia and Stripe to charge your payment method on a recurring basis at the frequency 
              associated with your selected plan. Subscription charges are assessed at the start of each billing period.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">2.3 Promotional and Bonus Credits</h4>
            <p className="text-slate-300 leading-relaxed">
              NewAudia may, at its sole discretion, grant promotional or bonus Credits as part of introductory offers, referral 
              programs, or other platform initiatives. Promotional Credits are subject to additional terms disclosed at the time 
              of the grant, including potential expiration dates, usage restrictions, and non-refundability. Promotional Credits 
              may not be combined with other offers unless expressly stated. NewAudia reserves the right to modify, limit, or 
              discontinue promotional Credit programs at any time without notice.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">2.4 Pricing Changes</h4>
            <p className="text-slate-300 leading-relaxed">
              Credit pricing, subscription rates, and plan structures may change at any time. NewAudia will use commercially 
              reasonable efforts to provide advance notice of material pricing changes via email to the address on file. Current 
              pricing is always available on the NewAudia Pricing page. Continued use of the platform following a pricing change 
              constitutes acceptance of the revised rates.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">3. Credit Consumption</h3>
            <p className="text-slate-300 leading-relaxed mb-6">
              Credits are consumed when Readers access content and features on the platform. The following rules govern consumption:
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">3.1 Consumption Triggers</h4>
            <p className="text-slate-300 leading-relaxed mb-3">
              Credits are deducted in connection with the following activities:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Accessing and listening to AI-narrated Audiobooks in the NewAudia library</li>
              <li>Using the Audia AI reading assistant to submit questions about Audiobook content</li>
              <li>Accessing premium or enhanced platform features as designated by NewAudia from time to time</li>
            </ul>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">3.2 Consumption Rates and Thresholds</h4>
            <p className="text-slate-300 leading-relaxed">
              Specific Credit consumption rates per activity are defined by NewAudia and are published within the platform at the 
              point of access. NewAudia reserves the right to adjust consumption rates at any time, subject to notice obligations 
              in Section 2.4. Partial sessions or interrupted usage may result in a full or proportional Credit deduction depending 
              on the activity type and the point at which access occurred.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">3.3 No Carry-Back for Consumed Credits</h4>
            <p className="text-slate-300 leading-relaxed">
              Credits that have been consumed in connection with a completed access session cannot be restored, reversed, or 
              refunded, except in cases of documented technical error as described in Section 5.2.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">4. Credit Expiration</h3>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">4.1 Purchased Credits</h4>
            <p className="text-slate-300 leading-relaxed">
              Credits acquired through individual purchase do not expire as long as the associated Reader account remains active 
              and in good standing. NewAudia reserves the right to introduce an expiration policy for purchased Credits upon no 
              less than thirty (30) days' written notice to affected account holders.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">4.2 Subscription Credits</h4>
            <p className="text-slate-300 leading-relaxed">
              Credits allocated as part of a subscription plan are valid for the billing period in which they are granted. Unused 
              subscription Credits do not roll over to subsequent billing periods unless expressly stated in the applicable 
              subscription plan terms. Cancellation of a subscription plan does not entitle the Reader to a refund of unused 
              subscription Credits for the current billing period.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">4.3 Promotional Credits</h4>
            <p className="text-slate-300 leading-relaxed">
              Promotional and bonus Credits are subject to expiration dates and usage restrictions disclosed at the time of the 
              grant. Expired promotional Credits are automatically removed from the account and cannot be restored.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">4.4 Credits Upon Account Closure or Ban</h4>
            <p className="text-slate-300 leading-relaxed">
              If a Reader account is voluntarily closed or permanently banned pursuant to the NewAudia Acceptable Use Policy or 
              Terms of Service, all remaining Credits — whether purchased, subscription-based, or promotional — are forfeited 
              without refund. See Section 6 for additional terms on termination and forfeiture.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">5. Billing, Refunds, and Adjustments</h3>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">5.1 All Purchases Are Non-Refundable</h4>
            <p className="text-slate-300 leading-relaxed">
              All Credit purchases and subscription payments are final and non-refundable, except as expressly required by 
              applicable law. Dissatisfaction with platform content, Audiobook quality, or AI-generated outputs does not constitute 
              grounds for a refund. NewAudia does not guarantee any specific listening experience, narration style, or Audia 
              response quality, consistent with Sections 2.5 and 3.2 of the Author Publishing Agreement.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">5.2 Technical Error Adjustments</h4>
            <p className="text-slate-300 leading-relaxed">
              NewAudia may, at its sole discretion, restore Credits that were improperly deducted as a result of a documented 
              platform error, system outage, or billing malfunction. To request a Credit adjustment, the Reader must submit a 
              written request to{' '}
              <a href="mailto:info@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">info@newaudia.com</a>
              {' '}within thirty (30) days of the date the erroneous deduction occurred, with sufficient detail to allow NewAudia 
              to investigate the claim. NewAudia is not obligated to restore Credits where the claimed error cannot be verified 
              through platform logs or billing records.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">5.3 Recurring Subscription Management</h4>
            <p className="text-slate-300 leading-relaxed">
              Readers may cancel a subscription plan at any time through the account settings on the NewAudia platform or by 
              contacting{' '}
              <a href="mailto:info@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">info@newaudia.com</a>. 
              Cancellation takes effect at the end of the current billing period. The Reader retains access to 
              subscription-allocated Credits through the end of the paid billing period. No refund is issued for the remaining 
              unused portion of the current billing period upon cancellation.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">5.4 Failed Payments</h4>
            <p className="text-slate-300 leading-relaxed">
              If a recurring subscription payment fails, NewAudia or Stripe may attempt to reprocess the charge. If payment 
              cannot be collected after reasonable retry attempts, NewAudia reserves the right to suspend or downgrade the 
              Reader's subscription access and withhold any Credit allocation associated with the unpaid billing period. The 
              Reader remains responsible for any outstanding balance. NewAudia will notify the Reader of a failed payment using 
              the email address on file.
            </p>

            <h4 className="text-lg font-medium text-white mt-6 mb-3">5.5 Abuse of Billing Systems</h4>
            <p className="text-slate-300 leading-relaxed">
              Any attempt to exploit, circumvent, or manipulate the Credit or billing system — including unauthorized chargebacks, 
              fraudulent payment submissions, or artificial inflation of Credit balances — is a violation of the NewAudia 
              Acceptable Use Policy and may result in immediate account suspension, forfeiture of all Credits, and permanent ban 
              from the platform. NewAudia reserves the right to pursue all available legal remedies in connection with billing 
              fraud or abuse.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">6. Credit Balance Disputes</h3>
            <p className="text-slate-300 leading-relaxed mb-3">
              If a Reader believes that Credits have been incorrectly deducted, improperly expired, or otherwise inaccurately 
              reflected in their account balance, the Reader must submit a written dispute to{' '}
              <a href="mailto:info@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">info@newaudia.com</a>
              {' '}within thirty (30) days of the date the disputed deduction or balance change occurred. Disputes submitted 
              after this period will not be considered.
            </p>
            <p className="text-slate-300 leading-relaxed mb-3">
              Dispute submissions should include the Reader's account email address, the date and approximate time of the 
              disputed deduction, the Audiobook title or feature involved, and any other detail that may assist NewAudia in 
              investigating the claim.
            </p>
            <p className="text-slate-300 leading-relaxed">
              NewAudia will review the dispute using available platform logs and billing records and will provide a written 
              response within a reasonable time. NewAudia's determination on Credit balance disputes is final.
            </p>
          </section>

          {/* Section 7 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">7. Suspension, Termination, and Credit Forfeiture</h3>
            <p className="text-slate-300 leading-relaxed mb-3">
              NewAudia reserves the right to suspend or terminate Reader access and forfeit Credits in the following 
              circumstances, consistent with the NewAudia Acceptable Use Policy:
            </p>
            <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
              <li>Misuse, manipulation, or artificial inflation of Credit balances</li>
              <li>Fraudulent payment activity, including unauthorized chargebacks</li>
              <li>Violation of the NewAudia Acceptable Use Policy or Terms of Service</li>
              <li>Conduct intended to game, exploit, or circumvent the Credit consumption or billing systems</li>
              <li>Account inactivity beyond a period established by NewAudia's retention policies, following notice to the Reader's email address on file</li>
            </ul>

            <div className="bg-red-950/30 border border-red-900/50 rounded-lg p-6 mt-6">
              <h4 className="text-lg font-semibold text-red-400 mb-3">Credit Forfeiture on Termination or Permanent Ban</h4>
              <p className="text-slate-300 leading-relaxed text-sm">
                If a Reader account is permanently banned pursuant to the NewAudia Acceptable Use Policy, all remaining Credits 
                — whether purchased, subscription-based, or promotional — are immediately and irrevocably forfeited. No refund 
                will be issued for forfeited Credits. This forfeiture is consistent with the enforcement provisions of the 
                Acceptable Use Policy and does not constitute a breach of any agreement between NewAudia and the Reader.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">8. No Guarantee of Availability</h3>
            <p className="text-slate-300 leading-relaxed mb-3">
              NewAudia does not guarantee uninterrupted access to the platform, continuous availability of any specific 
              Audiobook title, or perpetual operation of any Credit-consuming feature, including the Audia AI reading assistant. 
              NewAudia may modify, limit, or discontinue any feature or title at any time, with or without notice, consistent 
              with the Author Publishing Agreement and the applicable agreements governing platform content.
            </p>
            <p className="text-slate-300 leading-relaxed">
              In the event that a title is removed from the platform, NewAudia will use commercially reasonable efforts to 
              notify affected Readers and, at NewAudia's sole discretion, may issue a Credit adjustment for Credits spent on 
              that title within a reasonable period prior to removal. NewAudia is under no obligation to issue such an adjustment 
              and any such decision is made case-by-case.
            </p>
          </section>

          {/* Section 9 - Disclaimer */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">9. Disclaimer of Warranties</h3>
            <p className="text-slate-300 leading-relaxed text-sm uppercase tracking-wide">
              The Reader Credit system and associated billing features are provided "as is" and "as available" without warranty 
              of any kind, whether express, implied, or statutory. NewAudia disclaims all warranties, including but not limited 
              to implied warranties of merchantability, fitness for a particular purpose, and uninterrupted availability. NewAudia 
              does not warrant that Credit balances, consumption calculations, or billing records will be free from error at all 
              times, though NewAudia will use reasonable efforts to investigate and correct verified errors consistent with 
              Section 5.2.
            </p>
          </section>

          {/* Section 10 - Liability */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">10. Limitation of Liability</h3>
            <p className="text-slate-300 leading-relaxed text-sm uppercase tracking-wide">
              To the maximum extent permitted by applicable law, NewAudia's total cumulative liability to any Reader for any 
              claim arising under or related to this Policy shall not exceed the total amount paid by the Reader to NewAudia in 
              the three (3) months preceding the claim. NewAudia shall not be liable for any indirect, incidental, special, 
              consequential, exemplary, or punitive damages, including lost access, lost Credits, or lost data, even if advised 
              of the possibility of such damages.
            </p>
          </section>

          {/* Section 11 - Policy Updates */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">11. Policy Updates</h3>
            <p className="text-slate-300 leading-relaxed mb-6">
              NewAudia may update this Reader Credit & Billing Policy at any time by posting a revised version on the Services. 
              NewAudia will use reasonable efforts to notify users of material changes via email to the address on file. Continued 
              use of the Credit system following notice of any update constitutes acceptance of the revised Policy. The version 
              history below tracks material revisions.
            </p>

            <h4 className="text-lg font-medium text-white mb-3">Version History</h4>
            <div className="overflow-x-auto">
              <table data-testid="billing-version-history" className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300 w-20">Version</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300 w-36">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-300">Summary of Changes</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-slate-300">1.0</td>
                    <td className="py-3 px-4 text-slate-400">January 1, 2026</td>
                    <td className="py-3 px-4 text-slate-400">Initial publication. Entity listed as NewAudia dba Use What You Got Publishing, LLC. General Reader Credit terms and billing disclosures.</td>
                  </tr>
                  <tr className="border-b border-slate-800">
                    <td className="py-3 px-4 text-white font-medium">1.1</td>
                    <td className="py-3 px-4 text-slate-300">March 2026</td>
                    <td className="py-3 px-4 text-slate-400">
                      Entity updated to NewAudia Systems, LLC (registered trade name in Colorado). Added Company Identification 
                      section; expanded Credit nature definition and non-transferability; added Author Commission callout; 
                      expanded acquisition channels (individual, subscription, promotional, pricing changes); added consumption 
                      rules (triggers, rates, no carry-back); added expiration terms (purchased, subscription, promotional, 
                      account closure); expanded billing section (non-refundability, technical adjustments, subscription 
                      management, failed payments, billing abuse); added Credit balance disputes section; added suspension, 
                      termination, and forfeiture terms with permanent ban callout; added no-guarantee-of-availability section; 
                      added disclaimer of warranties and limitation of liability; added version history table.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Contact */}
          <section className="mb-10">
            <h3 className="text-xl font-semibold text-white mb-4">Contact Information</h3>
            <div data-testid="billing-contact-info" className="bg-slate-900/50 border border-slate-700 rounded-lg p-6">
              <p className="text-white font-semibold text-lg">NewAudia Systems, LLC</p>
              <p className="text-slate-400 text-sm">A Colorado Limited Liability Company</p>
              <p className="text-slate-400 text-sm">"NewAudia" is a registered trade name in Colorado</p>
              <p className="text-slate-400 mt-2">Colorado Springs, Colorado, USA</p>
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-indigo-400" />
                  <span className="text-slate-400 text-sm">General Inquiries & Billing Support:</span>
                  <a href="mailto:info@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors text-sm">
                    info@newaudia.com
                  </a>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-indigo-400" />
                  <span className="text-slate-400 text-sm">Administrative & Legal Notices:</span>
                  <a href="mailto:admin@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors text-sm">
                    admin@newaudia.com
                  </a>
                </div>
              </div>
              <p className="text-slate-400 text-sm mt-3">
                Website: <a href="https://www.newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">www.newaudia.com</a>
              </p>
            </div>
          </section>

          {/* Footer Entity Statement */}
          <section className="mt-12 pt-8 border-t border-slate-700">
            <p className="text-sm text-slate-500">
              NewAudia Systems, LLC | Colorado Springs, Colorado |{' '}
              <a href="https://www.newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">www.newaudia.com</a>
              {' '}| <a href="mailto:info@newaudia.com" className="text-indigo-400 hover:text-indigo-300 transition-colors">info@newaudia.com</a>
            </p>
            <p className="text-sm text-slate-500 mt-2 italic">
              "NewAudia" is a registered trade name of NewAudia Systems, LLC in the State of Colorado.
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Version 1.1 — March 2026
            </p>
          </section>
        </div>
      </main>

      <UniversalFooter />
    </div>
  );
};

export default ReaderCreditBillingPolicyPage;
